import React, { useEffect, useState } from "react";
import { api, imgUrl } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import SingleImageField from "../../components/common/SingleImageField";

const PLACEMENTS = ["home_hero", "home_strip", "announcement", "category_top", "seller_top", "checkout", "mobile_home"];
const blank = { title: "", subtitle: "", image_url: "", mobile_image_url: "", link_url: "", cta_label: "", placement: "home_hero", order: 0, is_active: true, start_at: "", end_at: "" };

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/banners");
      setBanners(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load banners");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title?.trim() || !form.image_url?.trim()) return toast.error("Title and image URL required");
    try {
      if (editingId) await api.put(`/admin/banners/${editingId}`, form);
      else await api.post("/admin/banners", form);
      toast.success(editingId ? "Updated" : "Created");
      setForm(blank); setEditingId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const edit = (b) => { setForm({ ...blank, ...b }); setEditingId(b.id); };

  const remove = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await api.delete(`/admin/banners/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const field = (k, placeholder, cls = "") => (
    <input
      className={`kb-input ${cls}`}
      placeholder={placeholder}
      value={form[k] || ""}
      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      data-testid={`banner-${k}`}
    />
  );

  return (
    <div data-testid="admin-banners-page">
      <div className="overline mb-2">Marketing</div>
      <h1 className="font-display text-4xl mb-8">Homepage Banners</h1>

      <div className="bg-white border border-[hsl(var(--kb-border))] p-6 mb-8">
        <div className="overline mb-4">{editingId ? "Edit Banner" : "New Banner"}</div>
        <div className="grid md:grid-cols-2 gap-3">
          {field("title", "Title")}
          {field("subtitle", "Subtitle (optional)")}
          <div className="md:col-span-2 grid md:grid-cols-2 gap-3">
            <SingleImageField
              label="Desktop image"
              value={form.image_url}
              onChange={(key) => setForm({ ...form, image_url: key || "" })}
              hint="Hero/wide image · WebP auto, 2048px max, ~80–150 KB"
              aspect="aspect-[16/9]"
              testid="banner-image-desktop"
            />
            <SingleImageField
              label="Mobile image (optional)"
              value={form.mobile_image_url}
              onChange={(key) => setForm({ ...form, mobile_image_url: key || "" })}
              hint="Portrait crop for mobile devices"
              aspect="aspect-[4/5]"
              testid="banner-image-mobile"
            />
          </div>
          {field("link_url", "Link URL — /shop/<cat> or /product/<slug> or https://…")}
          {field("cta_label", "CTA Label e.g. Shop Now")}
          <select className="kb-input" value={form.placement} onChange={(e) => setForm({ ...form, placement: e.target.value })} data-testid="banner-placement">
            {PLACEMENTS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input type="number" className="kb-input" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })} />
          <label className="text-xs">
            Start (optional)
            <input
              type="datetime-local"
              className="kb-input mt-1"
              value={form.start_at}
              onChange={(e) => setForm({ ...form, start_at: e.target.value })}
              data-testid="banner-start-at"
            />
          </label>
          <label className="text-xs">
            End (optional)
            <input
              type="datetime-local"
              className="kb-input mt-1"
              value={form.end_at}
              onChange={(e) => setForm({ ...form, end_at: e.target.value })}
              data-testid="banner-end-at"
            />
          </label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
        </div>
        <div className="flex gap-3 mt-4">
          <button className="kb-btn-primary" onClick={save} data-testid="save-banner-btn">{editingId ? "Update" : "Create"}</button>
          {editingId && <button className="kb-btn-outline" onClick={() => { setForm(blank); setEditingId(null); }}>Cancel</button>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {loading ? (
          <div className="col-span-2 text-center text-[hsl(var(--kb-muted))] p-6" data-testid="banners-loading">Loading banners…</div>
        ) : error ? (
          <div className="col-span-2 text-center text-red-700 p-6" data-testid="banners-error">{error}</div>
        ) : banners.length === 0 ? (
          <div className="text-[hsl(var(--kb-muted))] col-span-2">No banners yet.</div>
        ) : banners.map((b) => (
          <div key={b.id} className="bg-white border border-[hsl(var(--kb-border))] overflow-hidden" data-testid={`banner-row-${b.id}`}>
            <div className="aspect-[2/1] bg-[hsl(var(--kb-surface-alt))]">
              {b.image_url && <img src={imgUrl(b.image_url)} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-display text-xl">{b.title}</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))]">{b.placement} · order {b.order} · {b.is_active ? "active" : "inactive"}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => edit(b)} className="text-xs underline">Edit</button>
                  <button onClick={() => remove(b.id)} className="text-red-700" aria-label="Delete"><Trash2 size={14} /></button>
                </div>
              </div>
              {b.subtitle && <div className="text-sm text-[hsl(var(--kb-muted))]">{b.subtitle}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
